import { Component, Input, Output, EventEmitter, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';

import { AuthService, BasicTempUser } from 'app/services/auth.service';

import { fadeIn, fadeOut } from './input-animation';

/**
 * @deprecated - use email input
 *  this component is deprecated in favor of the email input component
 */
@Component({
  selector: 'sign-in-flag-input',
  templateUrl: './sign-in-flag.component.html',
  styleUrls: ['./sign-in-flag.component.scss'],
  animations: [fadeIn, fadeOut],
  standalone: false,
})
export class SignInFlagComponent implements OnDestroy, OnChanges {
  @Input() public resetInput = false;
  @Input() public confirmAccount = false;
  @Output() public inputEvent = new EventEmitter<string>();
  @Output() public countryCodeEvent = new EventEmitter<string>();

  private destroyed$ = new ReplaySubject<boolean>(1);
  private tempUser?: BasicTempUser;

  public selectedCountryCode = '+1';
  public showCountryCode = false;

  public phoneInput = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(4),
    Validators.pattern('^[0-9 +]*$'),
  ]);
  public phoneEmailInput = new UntypedFormControl('', [Validators.required, Validators.minLength(4)]);
  public countryCode = new UntypedFormControl(this.selectedCountryCode, [Validators.required, Validators.maxLength(4)]);

  public constructor(
    private store: Store,
    private authService: AuthService,
  ) {
    this.phoneInput.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe({
      next: () => this.validateInput(this.phoneInput.value, this.phoneInput.invalid),
    });

    this.phoneEmailInput.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.validateInput(this.phoneEmailInput.value, this.phoneEmailInput.invalid),
      });
  }

  // check for @Input() changes
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }

    if (changes.confirmAccount) {
      this.setTemporaryInput();
    }

    if (changes.resetInput) {
      this.phoneEmailInput.setValue('');
      this.phoneInput.setValue('');
      this.inputEvent.emit('');
      return;
    }
  }

  private validateInput(input: string, invalid: boolean): void {
    const inputLengthValid = input.length > 4;

    if (this.confirmAccount && this.tempUser) {
      return;
    }

    if (!inputLengthValid) {
      this.inputEvent?.emit('');
      this.showCountryCode = false;
      return;
    }

    if (invalid) {
      this.inputEvent?.emit('');
      this.showCountryCode = false;
      return;
    }

    if (input.includes('@') && inputLengthValid) {
      this.inputEvent?.emit(input);
      this.showCountryCode = false;
      return;
    }
    const isNumber = this.isNumber(input) && inputLengthValid;
    // If user has already input the us code do not show country code
    if (isNumber && input.includes('+1')) {
      this.inputEvent?.emit(input);
      this.showCountryCode = false;
    }

    if (isNumber && !input.includes('+1')) {
      this.showCountryCode = true;
      return;
    }
    this.showCountryCode = false;
  }

  private setTemporaryInput(): void {
    this.tempUser = this.authService.getUserSignUp();
    if (this.confirmAccount) {
      this.validateTempInput();
    }
  }

  private validateTempInput(): void {
    if (this.tempUser && this.tempUser['loginInput']) {
      this.inputEvent.emit(this.tempUser.loginInput);

      this.phoneEmailInput.setValue(this.tempUser.loginInput);
      this.phoneEmailInput.disable();
    }
  }

  private isNumber(n: string) {
    const number = n.replace(/[^0-9+ ]/g, '');
    return !isNaN(parseFloat(number)) && !isNaN(Number(number) - 0);
  }

  public ngOnDestroy(): void {
    this.inputEvent.emit('');
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
